import { FC, CSSProperties, useMemo } from "react";

import { cn } from "@src/lib/utils/cn";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";

type ToneName =
  | "blue"
  | "purple"
  | "gray"
  | "green"
  | "yellow"
  | "red"
  | "orange";

const initialsFontSize = (size: number) =>
  size <= 16
    ? 8
    : size <= 24
      ? 10
      : size <= 32
        ? 12
        : size <= 40
          ? 14
          : Math.round(size * 0.35);

const AVATAR_COLOR_NAMES: ToneName[] = [
  "blue",
  "purple",
  "green",
  "red",
  "orange",
  "gray",
];

const avatarColorClasses: Record<
  ToneName,
  { bg: string; text: string; border: string }
> = {
  blue: { bg: "bg-blue-1", text: "text-blue-8", border: "border-blue-1" },
  purple: {
    bg: "bg-purple-1",
    text: "text-purple-8",
    border: "border-purple-1",
  },
  green: { bg: "bg-green-1", text: "text-green-8", border: "border-green-1" },
  red: { bg: "bg-red-1", text: "text-red-8", border: "border-red-1" },
  orange: {
    bg: "bg-orange-1",
    text: "text-orange-8",
    border: "border-orange-1",
  },
  yellow: {
    bg: "bg-yellow-2",
    text: "text-yellow-10",
    border: "border-yellow-2",
  },
  gray: { bg: "bg-gray-1", text: "text-gray-8", border: "border-gray-1" },
};

type AvatarProfileImage = {
  thumbnailImageUrl?: string | null;
  imageUrls?: {
    thumb?: string | null;
    medium?: string | null;
    original?: string | null;
  } | null;
  mediaUrls?: { en?: string | null } | null;
};

type Props = {
  person: {
    name?: string | null | undefined;
    firstName?: string | null | undefined;
    lastName?: string | null | undefined;
    id: number;
    profileImage?: AvatarProfileImage | null;
  };
  color?: string;
  style?: CSSProperties;
  size?: number;
  showTooltip?: boolean;
  className?: string;
};

const Avatar: FC<Props> = ({
  color,
  style,
  person,
  size = 30,
  showTooltip = false,
  className,
}) => {
  const { name, firstName, lastName, id } = person;
  const profileImageEnabled = useFeatureFlag(Ff.ProfileImage);
  const profileImage = profileImageEnabled ? person.profileImage : null;
  const imageUrl = profileImage
    ? (size <= 48
        ? profileImage.thumbnailImageUrl || profileImage.imageUrls?.thumb
        : profileImage.imageUrls?.medium) ||
      profileImage.imageUrls?.original ||
      profileImage.mediaUrls?.en
    : null;
  const initial = name?.trim()[0]?.toUpperCase() || " ";
  const parts: string[] = useMemo(() => (name ? name?.split(" ") : []), [name]);
  let lastLetter = "";
  if (parts.length > 1) {
    lastLetter = parts[parts.length - 1].charAt(0).toUpperCase() || "";
  }
  const avatarColorIndex = id % AVATAR_COLOR_NAMES.length;
  const colorFromId = AVATAR_COLOR_NAMES[avatarColorIndex];
  const colorClasses = color
    ? avatarColorClasses[color as ToneName]
    : avatarColorClasses[colorFromId];

  const tooltipText = useMemo(() => {
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    if (firstName) {
      return firstName;
    }
    if (lastName) {
      return lastName;
    }
    return name || "";
  }, [firstName, lastName, name]);

  const avatarElement = (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full font-semibold select-none cursor-default border",
        colorClasses.bg,
        colorClasses.text,
        imageUrl ? "border-gray-2" : colorClasses.border,
        className,
      )}
      style={{
        height: size,
        width: size,
        fontSize: initialsFontSize(size),
        lineHeight: `${size}px`,
        ...(style || {}),
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={tooltipText}
          className="size-full rounded-full object-cover object-center"
        />
      ) : (
        <>
          {initial}
          {lastLetter}
        </>
      )}
    </div>
  );

  if (!showTooltip || !tooltipText) {
    return avatarElement;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{avatarElement}</TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default Avatar;
